/**
 * QuantTab — per-symbol quant analytics dashboard.
 *
 * Pulls 2 years of daily bars for the focus symbol + SPY (benchmark),
 * runs the full quant toolkit client-side, renders:
 *   - Big stat table (Sharpe / Sortino / Calmar / Beta / Alpha / VaR / CVaR / Skew / Kurt / HalfLife / TailRatio)
 *   - Returns distribution histogram with VaR + CVaR cutoff lines
 *   - Vol cone (realized vol percentiles across lookback windows)
 *   - Drawdown chart with deepest-DD bars highlighted
 *   - Regime classifier badge (risk-on / risk-off / panic / euphoria / choppy / calm)
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { LazyPlot as Plot } from '@/components/LazyPlot';
import { useStore, readThemeColors } from '@/store';
import { fetchBars } from '@/lib/bars';
import {
  sharpe, sortino, calmar, omega, informationRatio, treynor,
  historicalVaR, parametricVaR, cvar,
  beta, alpha, tailRatio, halfLife,
  skewness, kurtosis, drawdown, equityCurve,
  returns, logReturns, mean, stdev,
  volCone, regime,
} from '@/lib/quantMetrics';
import type { Bar } from '@/types';
import { ORG, MUT, FONT } from '@/theme';

const REGIME_COLOR: Record<string, string> = {
  'risk-on':  '#00cc66',
  'euphoria': '#ffee33',
  'risk-off': '#cc66ff',
  'panic':    '#ff3333',
  'choppy':   '#ff9900',
  'calm':     '#33aaff',
  'unknown':  '#555',
};

export function QuantTab() {
  const focus = useStore(s => s.focusSymbol);
  const theme = useStore(s => s.theme);
  void theme;

  const { gain: GAIN, loss: LOSS, accent: ACC } = readThemeColors();

  // Shared normalizing fetcher (lib/bars): coerces field names/units and drops
  // non-finite bars, so a `{bars:[...]}`-shaped or partial response can't crash
  // the metrics with a NaN close or a bad .map.
  const { data: bars, isError } = useQuery<Bar[]>({
    queryKey: ['bars-quant', focus],
    queryFn:  () => fetchBars(focus, '2y', '1d'),
    staleTime: 600_000,
  });

  const { data: spy } = useQuery<Bar[]>({
    queryKey: ['bars-quant', 'SPY'],
    queryFn:  () => fetchBars('SPY', '2y', '1d'),
    staleTime: 600_000,
  });

  const stats = useMemo(() => {
    if (!bars || bars.length < 30) return null;
    const closes = bars.map(b => b.close);
    const rets   = returns(closes);
    const logR   = logReturns(closes);
    const eq     = equityCurve(rets);
    const dd     = drawdown(eq);
    const benchRets = spy ? returns(spy.map(b => b.close)) : [];
    const cone   = volCone(logR);
    const reg    = regime(closes);
    return {
      rets, logR, eq, dd, cone, reg,
      ann_ret:    mean(rets) * 252,
      ann_vol:    stdev(rets) * Math.sqrt(252),
      sharpe:     sharpe(rets),
      sortino:    sortino(rets),
      calmar:     calmar(rets),
      omega:      omega(rets),
      // NaN, not 0, when the benchmark is unavailable. A failed SPY fetch
      // used to render BETA 0.00 / ALPHA 0.00% / INFO RATIO 0.00 — numbers
      // a trader would read as "this asset is market-neutral" rather than
      // "we couldn't compute this". The metrics themselves now align the
      // two series internally, so no pre-slicing here.
      info_ratio: benchRets.length > 30 ? informationRatio(rets, benchRets) : NaN,
      treynor:    benchRets.length > 30 ? treynor(rets, benchRets)          : NaN,
      beta:       benchRets.length > 30 ? beta(rets,    benchRets)          : NaN,
      alpha:      benchRets.length > 30 ? alpha(rets,   benchRets)          : NaN,
      var95_h:    historicalVaR(rets, 0.95),
      var99_h:    historicalVaR(rets, 0.99),
      var95_p:    parametricVaR(rets, 0.95),
      cvar95:     cvar(rets, 0.95),
      cvar99:     cvar(rets, 0.99),
      skew:       skewness(rets),
      kurt:       kurtosis(rets),
      tail:       tailRatio(rets),
      halflife:   halfLife(closes),
      max_dd:     dd.max,
      dd_dur:     dd.maxDurationBars,
    };
  }, [bars, spy]);

  // A failed fetch is not "still loading". Without this branch the tab sat on
  // "LOADING BARS..." forever after an error, indistinguishable from a slow
  // network -- a failure rendering as a (pending) fact.
  if (isError) return <Cent>ANALYTICS UNAVAILABLE — BARS FETCH FAILED</Cent>;
  if (!bars) return <Cent>LOADING BARS FOR {focus}...</Cent>;
  if (!stats) return <Cent>NEED AT LEAST 30 BARS FOR ANALYTICS</Cent>;

  return (
    <div style={{ fontFamily: FONT, color: '#e0e0e0' }}>
      {/* Regime badge */}
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 8 }}>
        <span style={{ color: MUT, fontSize: 10, letterSpacing: 1 }}>REGIME:</span>
        <span style={{
          color: REGIME_COLOR[stats.reg], fontSize: 11, fontWeight: 'bold',
          padding: '3px 10px', border: '1px solid ' + REGIME_COLOR[stats.reg],
          borderRadius: 2, letterSpacing: 1, textTransform: 'uppercase',
        }}>{stats.reg.replace('-', ' ')}</span>
        <span style={{ color: MUT, fontSize: 10, marginLeft: 12 }}>
          half-life: {Number.isFinite(stats.halflife) ? `${stats.halflife.toFixed(1)}d` : 'persistent'}
        </span>
      </div>

      {/* Three rows of stats */}
      <StatRow label="RETURN"   stats={[
        ['ANN RET',   pct(stats.ann_ret),     stats.ann_ret  >= 0 ? GAIN : LOSS],
        ['ANN VOL',   pct(stats.ann_vol)],
        ['SHARPE',    num(stats.sharpe),  gradeOrNull(stats.sharpe, GAIN, ACC, LOSS)],
        ['SORTINO',   num(stats.sortino), gradeOrNull(stats.sortino, GAIN, ACC, LOSS)],
        ['CALMAR',    num(stats.calmar),  gradeOrNull(stats.calmar, GAIN, ACC, LOSS)],
        ['OMEGA',     Number.isFinite(stats.omega) ? stats.omega.toFixed(2) : '∞'],
      ]} />
      <StatRow label="VS SPY"   stats={[
        ['BETA',      num(stats.beta)],
        ['ALPHA',     Number.isFinite(stats.alpha) ? pct(stats.alpha) : DASH,
                      Number.isFinite(stats.alpha) ? (stats.alpha >= 0 ? GAIN : LOSS) : undefined],
        ['INFO RATIO',num(stats.info_ratio), gradeOrNull(stats.info_ratio, GAIN, ACC, LOSS)],
        ['TREYNOR',   Number.isFinite(stats.treynor) ? pct(stats.treynor) : DASH],
      ]} />
      <StatRow label="RISK"     stats={[
        // These are loss magnitudes, so they render with a leading minus.
        // A hardcoded "-" produced "--0.50%" on an all-up window where the
        // underlying value is already negative. neg() handles the sign.
        ['VaR 95',    neg(stats.var95_h), LOSS],
        ['VaR 99',    neg(stats.var99_h), LOSS],
        ['CVaR 95',   neg(stats.cvar95),  LOSS],
        ['CVaR 99',   neg(stats.cvar99),  LOSS],
        ['MAX DD',    neg(stats.max_dd),  LOSS],
        ['DD DUR',    `${stats.dd_dur}d`],
        ['SKEW',      stats.skew.toFixed(2), stats.skew >= 0 ? GAIN : LOSS],
        ['KURT',      stats.kurt.toFixed(2)],
        ['TAIL',      num(stats.tail)],
      ]} />

      {/* Charts: distribution + vol cone */}
      <div style={{ display: 'flex', gap: 12, marginTop: 10, height: 220 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ color: ORG, fontSize: 10, letterSpacing: 1, marginBottom: 2 }}>
            DAILY RETURNS DISTRIBUTION
          </div>
          <Plot
            data={[{
              type: 'histogram', name: 'rets',
              x: stats.rets.map(r => r * 100),
              nbinsx: 50,
              marker: { color: ACC, opacity: 0.7 },
            } as unknown as Plotly.Data]}
            layout={{
              paper_bgcolor: '#090909', plot_bgcolor: '#090909',
              margin: { l: 40, r: 10, t: 4, b: 24 },
              font: { color: '#e0e0e0', family: 'Consolas, monospace', size: 9 },
              xaxis: { gridcolor: '#1a1a1a', color: MUT, ticksuffix: '%' },
              yaxis: { gridcolor: '#1a1a1a', color: MUT },
              showlegend: false,
              shapes: [
                { type: 'line', xref: 'x', x0: -stats.var95_h * 100, x1: -stats.var95_h * 100, yref: 'paper', y0: 0, y1: 1,
                  line: { color: LOSS, width: 1, dash: 'dash' } },
                { type: 'line', xref: 'x', x0: -stats.cvar95  * 100, x1: -stats.cvar95  * 100, yref: 'paper', y0: 0, y1: 1,
                  line: { color: '#cc66ff', width: 1, dash: 'longdash' } },
              ],
            } as unknown as Partial<Plotly.Layout>}
            config={{ displayModeBar: false, responsive: true }}
            style={{ width: '100%', height: '100%' }}
            useResizeHandler
          />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ color: ORG, fontSize: 10, letterSpacing: 1, marginBottom: 2 }}>
            VOLATILITY CONE
          </div>
          <Plot
            data={[
              { type: 'scatter', mode: 'lines+markers', name: '95%ile',
                x: stats.cone.map(c => c.window), y: stats.cone.map(c => c.p95 * 100),
                line: { color: LOSS, width: 1, dash: 'dot' }, marker: { size: 5, color: LOSS } },
              { type: 'scatter', mode: 'lines+markers', name: '75%ile',
                x: stats.cone.map(c => c.window), y: stats.cone.map(c => c.p75 * 100),
                line: { color: '#ff9900', width: 1, dash: 'dot' }, marker: { size: 5, color: '#ff9900' } },
              { type: 'scatter', mode: 'lines+markers', name: 'median',
                x: stats.cone.map(c => c.window), y: stats.cone.map(c => c.p50 * 100),
                line: { color: '#888', width: 1.4 }, marker: { size: 5, color: '#888' } },
              { type: 'scatter', mode: 'lines+markers', name: '25%ile',
                x: stats.cone.map(c => c.window), y: stats.cone.map(c => c.p25 * 100),
                line: { color: GAIN, width: 1, dash: 'dot' }, marker: { size: 5, color: GAIN } },
              { type: 'scatter', mode: 'markers', name: 'current',
                x: stats.cone.map(c => c.window), y: stats.cone.map(c => c.current * 100),
                marker: { size: 9, color: ACC, symbol: 'diamond' } },
            ] as unknown as Plotly.Data[]}
            layout={{
              paper_bgcolor: '#090909', plot_bgcolor: '#090909',
              margin: { l: 40, r: 10, t: 4, b: 24 },
              font: { color: '#e0e0e0', family: 'Consolas, monospace', size: 9 },
              xaxis: { gridcolor: '#1a1a1a', color: MUT, title: { text: 'WINDOW (BARS)', font: { size: 8 } } },
              yaxis: { gridcolor: '#1a1a1a', color: MUT, ticksuffix: '%' },
              showlegend: false,
            } as unknown as Partial<Plotly.Layout>}
            config={{ displayModeBar: false, responsive: true }}
            style={{ width: '100%', height: '100%' }}
            useResizeHandler
          />
        </div>
      </div>

      {/* Drawdown chart */}
      <div style={{ height: 160, marginTop: 10 }}>
        <div style={{ color: ORG, fontSize: 10, letterSpacing: 1, marginBottom: 2 }}>
          UNDERWATER CHART (DRAWDOWN OVER TIME)
        </div>
        <Plot
          data={[{
            type: 'scatter', mode: 'lines', name: 'DD',
            // equityCurve now seeds at 1.0, so dd.series has one MORE
            // point than `rets` (i.e. one per bar, including the first).
            // Pairing it with bars.slice(1) shifted the whole drawdown
            // curve a bar later and dropped the final point.
            // toIsoDay handles seconds-vs-milliseconds: futures bars stamp
            // time in ms, and `b.time * 1000` on those produced year-55000
            // dates on the axis.
            x: bars.map(b => toIsoDay(b.time)),
            y: stats.dd.series.map(d => d * 100),
            line: { color: LOSS, width: 1.2 },
            fill: 'tozeroy', fillcolor: 'rgba(255,51,51,0.15)',
          } as unknown as Plotly.Data]}
          layout={{
            paper_bgcolor: '#090909', plot_bgcolor: '#090909',
            margin: { l: 50, r: 10, t: 0, b: 24 },
            font: { color: '#e0e0e0', family: 'Consolas, monospace', size: 9 },
            xaxis: { gridcolor: '#1a1a1a', color: MUT },
            yaxis: { gridcolor: '#1a1a1a', color: MUT, ticksuffix: '%' },
            showlegend: false,
          } as unknown as Partial<Plotly.Layout>}
          config={{ displayModeBar: false, responsive: true }}
          style={{ width: '100%', height: '100%' }}
          useResizeHandler
        />
      </div>
    </div>
  );
}

// ── Helpers ──────────────────────────────────────────────────────────────

function pct(n: number): string {
  if (!Number.isFinite(n)) return '—';
  return `${(n * 100).toFixed(2)}%`;
}

function grade(value: number, good: string, ok: string, bad: string): string {
  if (!Number.isFinite(value)) return '#888';
  if (value >= 1) return good;
  if (value >= 0) return ok;
  return bad;
}

// Placeholder for a metric that is genuinely undefined (too few samples, no
// benchmark, zero downside deviation). Rendering "0.00" for these was the
// bug: an absent number is not a bad number, and colouring it red told the
// trader something false about their strategy.
const DASH = '—';

// Render a loss magnitude with a single, correct leading sign.
function neg(n: number): string {
  if (!Number.isFinite(n)) return DASH;
  return n === 0 ? '0.00%' : `${n > 0 ? '-' : ''}${pct(n)}`;
}

function num(n: number, dp = 2): string {
  return Number.isFinite(n) ? n.toFixed(dp) : DASH;
}

// Bar time may be UNIX seconds (stocks) or milliseconds (futures). Normalize to
// ms before formatting so a futures series doesn't render year-55000 dates.
function toIsoDay(t: number): string {
  if (!Number.isFinite(t) || t <= 0) return '';
  const ms = t < 1e12 ? t * 1000 : t;
  const d = new Date(ms);
  return Number.isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10);
}

// Only grade a value that actually exists; leave undefined ones neutral.
// Colours are theme-scoped, so they're passed in rather than closed over.
function gradeOrNull(value: number, good: string, ok: string, bad: string): string {
  return Number.isFinite(value) ? grade(value, good, ok, bad) : '#888';
}

function StatRow({ label, stats }: { label: string; stats: Array<[string, string, string?]> }) {
  return (
    <div style={{
      display: 'flex', gap: 18, marginBottom: 4,
      padding: '4px 0', borderBottom: '1px solid #222', fontSize: 11, flexWrap: 'wrap',
      alignItems: 'baseline',
    }}>
      <span style={{ color: ORG, fontSize: 9, letterSpacing: 2, minWidth: 64 }}>{label}</span>
      {stats.map(([k, v, c]) => (
        <span key={k}>
          <span style={{ color: MUT, fontSize: 9, letterSpacing: 1, marginRight: 4 }}>{k}</span>
          <span style={{ color: c ?? '#e0e0e0', fontWeight: 'bold', fontSize: 11 }}>{v}</span>
        </span>
      ))}
    </div>
  );
}

function Cent({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      height: '100%', color: '#333', fontSize: 11, letterSpacing: 2, fontFamily: FONT,
    }}>{children}</div>
  );
}
